import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Account, AccountContact } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import { ProposalStatus, proposalApi } from '@/api/commercial/proposals';
import { useToast } from '@/hooks/use-toast';

export interface LocalProposal {
  id?: string;
  clientId: string;
  clientName?: string;
  clientCpfCnpj: string;
  status: ProposalStatus;
  creditAnalysisId?: string | null;
}

function getSelectedContact(contacts: AccountContact[], selectedContactIds: Set<string>) {
  const selectedId = Array.from(selectedContactIds)[0];
  if (!selectedId) return null;
  return contacts.find((contact) => contact.id === selectedId) ?? null;
}

function normalizeSelectedContactIds(ids: Iterable<string>): Set<string> {
  const selectedId = Array.from(ids)[0];
  return selectedId ? new Set([selectedId]) : new Set<string>();
}

const STORAGE_KEYS = {
  ACCOUNT: 'create_proposal_flow_account',
  CONTACTS: 'create_proposal_flow_contacts',
  SELECTED_CONTACT_IDS: 'create_proposal_flow_selected_contact_ids',
  PROPOSAL: 'create_proposal_flow_proposal',
  CREDIT_ANALYSIS: 'create_proposal_flow_credit_analysis',
} as const;

function getFromStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;

  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Failed to parse ${key} from localStorage:`, error);
    return defaultValue;
  }
}

function setToStorage<T>(key: string, value: T): boolean {
  if (typeof window === 'undefined') return false;

  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
    return false;
  }
}

function removeFromStorage(key: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove ${key} from localStorage:`, error);
  }
}

interface CreateProposalFlowContextType {
  // State
  account: Account | null;
  contacts: AccountContact[];
  selectedContactIds: Set<string>;
  proposal: LocalProposal | null;
  creditAnalysis: CreditAnalysis | null;

  // Account methods
  setAccount: (account: Account | null) => void;

  // Contact methods
  setContacts: (contacts: AccountContact[]) => void;
  setSelectedContactIds: Dispatch<SetStateAction<Set<string>>>;
  toggleContactSelection: (contactId: string) => void;

  // Proposal methods
  setProposal: (proposal: LocalProposal | null) => void;

  // Credit analysis methods
  setCreditAnalysis: (creditAnalysis: CreditAnalysis | null) => void;

  // Utility methods
  resetFlow: () => void;

  // API creation method
  createProposalApi: () => Promise<{
    success: boolean;
    proposalId?: string;
    error?: string;
  }>;
}

const CreateProposalFlowContext = createContext<CreateProposalFlowContextType>(
  {} as CreateProposalFlowContextType,
);

interface CreateProposalFlowProviderProps {
  children: ReactNode;
  resetOnMount?: boolean;
}

export function CreateProposalFlowProvider({
  children,
  resetOnMount = false,
}: CreateProposalFlowProviderProps) {
  const { toast } = useToast();

  // State to hold the current localStorage data
  const [currentData, setCurrentData] = useState({
    account: null as Account | null,
    contacts: [] as AccountContact[],
    selectedContactIds: new Set<string>(),
    proposal: null as LocalProposal | null,
    creditAnalysis: null as CreditAnalysis | null,
  });

  // Function to load data from localStorage
  const loadDataFromLocalStorage = useCallback(() => {
    const storedSelectedIds = getFromStorage<string[]>(STORAGE_KEYS.SELECTED_CONTACT_IDS, []);

    const newData = {
      account: getFromStorage<Account | null>(STORAGE_KEYS.ACCOUNT, null),
      contacts: getFromStorage<AccountContact[]>(STORAGE_KEYS.CONTACTS, []),
      selectedContactIds: normalizeSelectedContactIds(storedSelectedIds),
      proposal: getFromStorage<LocalProposal | null>(STORAGE_KEYS.PROPOSAL, null),
      creditAnalysis: getFromStorage<CreditAnalysis | null>(STORAGE_KEYS.CREDIT_ANALYSIS, null),
    };

    setCurrentData(newData);
  }, []);

  // Load data on mount and when localStorage changes
  useEffect(() => {
    loadDataFromLocalStorage();
    // eslint-disable-next-line react-hooks/set-state-in-effect
  }, [loadDataFromLocalStorage]);

  // Listen for localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = () => {
      loadDataFromLocalStorage();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/set-state-in-effect
  }, [loadDataFromLocalStorage]);

  // Setter functions
  const setAccount = useCallback(
    (newAccount: Account | null) => {
      if (newAccount && typeof newAccount === 'object') {
        const success = setToStorage<Account>(STORAGE_KEYS.ACCOUNT, newAccount);
        if (!success) {
          toast({
            title: 'Erro ao salvar',
            description:
              'Não foi possível salvar os dados da conta. Verifique o espaço disponível no navegador.',
            variant: 'destructive',
          });
        }
      } else {
        removeFromStorage(STORAGE_KEYS.ACCOUNT);
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage, toast],
  );

  const setContacts = useCallback(
    (newContacts: AccountContact[]) => {
      if (Array.isArray(newContacts)) {
        const success = setToStorage<AccountContact[]>(STORAGE_KEYS.CONTACTS, newContacts);
        if (!success) {
          toast({
            title: 'Erro ao salvar',
            description:
              'Não foi possível salvar os dados dos contatos. Verifique o espaço disponível no navegador.',
            variant: 'destructive',
          });
        }
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage, toast],
  );

  const setSelectedContactIds: Dispatch<SetStateAction<Set<string>>> = useCallback((action) => {
    setCurrentData((prev) => {
      const newIds = typeof action === 'function' ? action(prev.selectedContactIds) : action;
      const normalizedIds = normalizeSelectedContactIds(newIds);
      setToStorage<string[]>(STORAGE_KEYS.SELECTED_CONTACT_IDS, Array.from(normalizedIds));
      return { ...prev, selectedContactIds: normalizedIds };
    });
  }, []);

  const toggleContactSelection = useCallback(
    (contactId: string) => {
      setSelectedContactIds((prev) =>
        prev.has(contactId) ? new Set<string>() : new Set([contactId]),
      );
    },
    [setSelectedContactIds],
  );

  const setProposal = useCallback(
    (newProposal: LocalProposal | null) => {
      if (newProposal && typeof newProposal === 'object') {
        const success = setToStorage<LocalProposal>(STORAGE_KEYS.PROPOSAL, newProposal);
        if (!success) {
          toast({
            title: 'Erro ao salvar',
            description:
              'Não foi possível salvar os dados da proposta. Verifique o espaço disponível no navegador.',
            variant: 'destructive',
          });
        }
      } else {
        removeFromStorage(STORAGE_KEYS.PROPOSAL);
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage, toast],
  );

  const setCreditAnalysis = useCallback(
    (newCreditAnalysis: CreditAnalysis | null) => {
      if (newCreditAnalysis && typeof newCreditAnalysis === 'object') {
        const success = setToStorage<CreditAnalysis>(
          STORAGE_KEYS.CREDIT_ANALYSIS,
          newCreditAnalysis,
        );
        if (!success) {
          toast({
            title: 'Erro ao salvar',
            description:
              'Não foi possível salvar os dados da análise de crédito. Verifique o espaço disponível no navegador.',
            variant: 'destructive',
          });
        }
      } else {
        removeFromStorage(STORAGE_KEYS.CREDIT_ANALYSIS);
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage, toast],
  );

  // Utility methods
  const resetFlow = useCallback(() => {
    removeFromStorage(STORAGE_KEYS.ACCOUNT);
    removeFromStorage(STORAGE_KEYS.CONTACTS);
    removeFromStorage(STORAGE_KEYS.SELECTED_CONTACT_IDS);
    removeFromStorage(STORAGE_KEYS.PROPOSAL);
    removeFromStorage(STORAGE_KEYS.CREDIT_ANALYSIS);

    setCurrentData({
      account: null,
      contacts: [],
      selectedContactIds: new Set<string>(),
      proposal: null,
      creditAnalysis: null,
    });
  }, []);

  // Reset flow on mount if requested (for "Nova Proposta" button clicks)
  useEffect(() => {
    if (resetOnMount) {
      resetFlow();
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
  }, [resetOnMount, resetFlow]);

  // API creation method
  const createProposalApi = useCallback(async () => {
    try {
      const { account, contacts, selectedContactIds, proposal: existingProposal } = currentData;

      if (!account?.id || !account?.document?.value) {
        return {
          success: false,
          error: 'Selecione uma conta antes de continuar',
        };
      }

      if (selectedContactIds.size === 0) {
        return {
          success: false,
          error: 'Selecione pelo menos um contato',
        };
      }

      const selectedContact = getSelectedContact(contacts, selectedContactIds);
      if (!selectedContact) {
        return {
          success: false,
          error: 'Contato selecionado não encontrado',
        };
      }

      // Se já existe uma proposta, apenas retorna sucesso sem recriar
      if (existingProposal?.id) {
        await proposalApi.update(existingProposal.id, { contact: selectedContact });
        return {
          success: true,
          proposalId: existingProposal.id,
        };
      }

      const result = await proposalApi.create({
        clientId: account.id,
        clientName: account.social_name || account.name,
        clientCpfCnpj: account.document.value,
        status: ProposalStatus.OPEN,
        contact: selectedContact,
      });

      if (result.success && result.id) {
        const newProposal: LocalProposal = {
          id: result.id,
          clientId: account.id,
          clientName: account.social_name || account.name,
          clientCpfCnpj: account.document.value,
          status: ProposalStatus.OPEN,
        };
        setProposal(newProposal);

        toast({
          variant: 'success',
          title: 'Proposta criada',
          description: 'A proposta foi criada com sucesso!',
        });

        return {
          success: true,
          proposalId: result.id,
        };
      }

      return {
        success: false,
        error: 'Erro ao criar proposta',
      };
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao criar a proposta. Tente novamente.',
        variant: 'destructive',
      });
      return {
        success: false,
        error: 'Erro inesperado ao criar proposta',
      };
    }
  }, [currentData, setProposal, toast]);

  // Memoize context value
  const value: CreateProposalFlowContextType = useMemo(
    () => ({
      // State
      account: currentData.account,
      contacts: currentData.contacts,
      selectedContactIds: currentData.selectedContactIds,
      proposal: currentData.proposal,
      creditAnalysis: currentData.creditAnalysis,

      // Account methods
      setAccount,

      // Contact methods
      setContacts,
      setSelectedContactIds,
      toggleContactSelection,

      // Proposal methods
      setProposal,

      // Credit analysis methods
      setCreditAnalysis,

      // Utility methods
      resetFlow,

      // API creation method
      createProposalApi,
    }),
    [
      currentData,
      setAccount,
      setContacts,
      setSelectedContactIds,
      toggleContactSelection,
      setProposal,
      setCreditAnalysis,
      resetFlow,
      createProposalApi,
    ],
  );

  return (
    <CreateProposalFlowContext.Provider value={value}>
      {children}
    </CreateProposalFlowContext.Provider>
  );
}

export function useCreateProposalFlow() {
  const context = useContext(CreateProposalFlowContext);
  if (!context) {
    throw new Error('useCreateProposalFlow must be used within a CreateProposalFlowProvider');
  }
  return context;
}
